import { useCallback } from 'react';
import { Locale } from '@headless-workspace/config';
import { CookieName } from '@headless-workspace/core-domain';
import { CookieHelpers } from '@headless-workspace/utils';
import {
    ClientEnvKey,
    DataLayerKey,
    useClientData,
    useDataLayer,
    useDeviceType,
    usePotionCookieManager,
    useUserInfo,
} from '../../../../client';
import { getConnectedUserInfo } from '../../../../server';
import { DataLayerVendorsConsent } from '../ConsentCookieDataLayerParams';
import { useHeaderDataLayerVariables } from './useHeaderDataLayerVariables';

const POTION_VENDORS_ID = 50;

interface UseHeaderDataLayerProps {
    addVariables: ReturnType<typeof useDataLayer>['addVariables'];
    deleteVariable: ReturnType<typeof useDataLayer>['deleteVariable'];
    locale: Locale;
}

export const useHeaderDataLayer = ({ addVariables, deleteVariable, locale }: UseHeaderDataLayerProps) => {
    const device = useDeviceType();
    const { getClientEnvVariable } = useClientData();
    const buildEnv = getClientEnvVariable(ClientEnvKey.BuildEnvironment);
    const { result: userInfoResult, isLoading: isUserInfoLoading } = useUserInfo();
    const { deletePotionCookie, setPotionCookie } = usePotionCookieManager(locale);
    const userInfo = getConnectedUserInfo(userInfoResult);
    const userStatus = userInfo?.status;
    const isSessionInitialized = !isUserInfoLoading && userInfoResult !== undefined;
    // set basic variables
    useHeaderDataLayerVariables({
        locale,
        device,
        buildEnv,
        addVariables,
        userInfo,
    });

    const setDataLayerPotionGuestId = useCallback(() => {
        const potionCookie = CookieHelpers.getClientSideCookie(CookieName.PotionGuestId);
        if (potionCookie) {
            addVariables({ [DataLayerKey.PotionGuestId]: potionCookie });
        }
    }, [addVariables]);

    const managePotionGuestId = useCallback(
        async (result: DataLayerVendorsConsent) => {
            const { vendors } = result.consent;
            const analyticsPotion = vendors[POTION_VENDORS_ID];

            if (!analyticsPotion || analyticsPotion?.status === 'off') {
                deletePotionCookie();
                deleteVariable(DataLayerKey.PotionGuestId);
                return;
            }

            const potionCookie = CookieHelpers.getClientSideCookie(CookieName.PotionGuestId);
            if (!potionCookie) {
                await setPotionCookie();
                setDataLayerPotionGuestId();
            }
        },
        [deletePotionCookie, deleteVariable, setPotionCookie, setDataLayerPotionGuestId],
    );

    return {
        userStatus,
        isSessionInitialized,
        setDataLayerPotionGuestId,
        managePotionGuestId,
    };
};
